import axios from 'axios';
import { getBaseUrl } from 'utils/url';

/** Legacy API values plus newer labels (same tiers: Priority≈High, Engage≈Medium, Explore≈Low). */
export type CosellingIntentStrength =
  | 'High'
  | 'Medium'
  | 'Low'
  | 'Priority'
  | 'Engage'
  | 'Explore';

export type CosellingIntentTier = 'high' | 'medium' | 'low' | 'unknown';

export function cosellingIntentStrengthTier(
  intentStrength: string,
): CosellingIntentTier {
  switch (intentStrength) {
    case 'High':
    case 'Priority':
      return 'high';
    case 'Medium':
    case 'Engage':
      return 'medium';
    case 'Low':
    case 'Explore':
      return 'low';
    default:
      return 'unknown';
  }
}

/** User-facing label: maps legacy (High/Medium/Low) and new API values to Priority / Engage / Explore. */
export function cosellingIntentStrengthDisplayLabel(
  intentStrength: string,
): string {
  switch (cosellingIntentStrengthTier(intentStrength)) {
    case 'high':
      return 'Priority';
    case 'medium':
      return 'Engage';
    case 'low':
      return 'Explore';
    default:
      return intentStrength;
  }
}

/**
 * Strips markdown-style inline citations from compact fields (e.g. companySize, location, industry),
 * such as ` ([jobs.example.com](https://...))` or trailing `[label](url)`.
 */
export function cosellingStripInlineMarkdownSources(text: string): string {
  if (!text) {
    return text;
  }
  let s = text;
  const parenWrapped = /\s*\(\[[^\]]+\]\([^)]+\)\)/g;
  const bareLink = /\s*\[[^\]]+\]\([^)]+\)/g;
  let prev = '';
  while (s !== prev) {
    prev = s;
    s = s.replace(parenWrapped, '').replace(bareLink, '');
  }
  return s.replace(/\s+/g, ' ').trim();
}

export interface CosellingRecommendation {
  companyName: string;
  domain: string;
  ecosystemSignal: string;
  intentStrength: CosellingIntentStrength;
  partnerSources: string;
  confidencePercent: number;
  confidenceReason?: string;
  companySize: string;
  industry: string;
  location: string;
  techOverlapCount: number;
  intentSignalCount: number;
  dataSource: string;
  isPlaceholder?: boolean;
}

export interface CosellingSummary {
  totalProspects: number;
  highFitProspects: number;
  mediumFitProspects: number;
  directPartnerData: number;
  inferredEcosystem: number;
  extendedSources: number;
  fallbacksUsed: string[];
  keyInsights: string[];
  userFacingSummary: string;
}

export interface CosellingResponse {
  success: boolean;
  message: string;
  data: {
    prospects: CosellingRecommendation[];
    partnersAnalyzed?: {
      partnerName: string;
      partnerUrl: string;
      customersFound: number;
      dataQuality: string;
    }[];
    summary: CosellingSummary;
  };
  metadata: {
    model?: string;
    usage?: {
      prompt_tokens: number;
      completion_tokens: number;
      total_tokens: number;
      prompt_tokens_details: {
        cached_tokens: number;
        audio_tokens: number;
      };
      completion_tokens_details: {
        reasoning_tokens: number;
        audio_tokens: number;
        accepted_prediction_tokens: number;
        rejected_prediction_tokens: number;
      };
    };
    generatedAt?: string;
    summaryGeneratedAt?: string;
    retrievedAt?: string;
    nextRegenerationDate?: string;
    fromCache?: boolean;
    accountId?: string;
    subscription?: Record<string, unknown>;
    usedCompanyData?: {
      userCompany: string;
      userIndustry: string;
      preferredRegions: string;
      customerWebsite: string | null;
      version: string;
    };
    generatedData?: {
      userProducts: string;
      targetMarket: string;
      productsGenerated: boolean;
      targetMarketGenerated: boolean;
    };
    icpData?: {
      targetIndustries: string[];
      targetCompanySizes: string[];
      targetGeographies: string[];
      wasGenerated: boolean;
    };
    requestId?: string;
    userId?: string;
  };
}

export type CosellingWithProfileJobStatus =
  | 'pending'
  | 'processing'
  | 'completed'
  | 'failed';

export interface CosellingWithProfileJobStartResponse {
  success: boolean;
  message: string;
  jobId: string;
}

export interface CosellingWithProfileJobValidationErrorBody {
  success: false;
  message: string;
  errors?: string[];
}

type CosellingWithProfileJobPollBody =
  | {
      success: true;
      jobId: string;
      status: 'pending' | 'processing';
      message: string;
    }
  | {
      success: true;
      jobId: string;
      status: 'completed';
      message: string;
      data: CosellingResponse['data'];
      metadata: CosellingResponse['metadata'];
    }
  | {
      success: true;
      jobId: string;
      status: 'failed';
      message: string;
      error: { code: string; message: string };
    }
  | { success: false; message: string };

const WITH_PROFILE_POLL_INTERVAL_MS = 3000;
const WITH_PROFILE_MAX_WAIT_MS = 2 * 60 * 60 * 1000;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export interface CosellingRequest {
  userProducts: string;
  targetMarket: string;
  preferredRegions?: string;
  maxRecommendations?: number;
  userCompany?: string;
  userIndustry?: string;
}

export class CosellingService {
  /**
   * Generate co-selling recommendations with explicit company information
   */
  static async generateRecommendations(
    token: string,
    request: CosellingRequest,
  ): Promise<CosellingResponse> {
    try {
      const response = await axios.post(
        `${getBaseUrl()}/api/coselling/recommendations`,
        request,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error('Error generating co-selling recommendations:', error);
      throw error;
    }
  }

  /**
   * Generate recommendations using company information from user's account profile.
   * Starts an async job (202) and polls until completed, failed, or timeout.
   */
  static async generateRecommendationsWithProfile(
    token: string,
    options?: { maxRecommendations?: number },
  ): Promise<CosellingResponse> {
    const baseUrl = `${getBaseUrl()}/api/coselling/recommendations/with-profile`;
    const headers = {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };

    try {
      const start = await axios.post<
        CosellingWithProfileJobStartResponse | CosellingWithProfileJobValidationErrorBody
      >(
        baseUrl,
        options?.maxRecommendations != null
          ? { maxRecommendations: options.maxRecommendations }
          : {},
        {
          headers,
          validateStatus: (status) => status === 202 || status === 400,
        },
      );

      if (start.status === 400) {
        const body = start.data as CosellingWithProfileJobValidationErrorBody;
        const detail =
          body.errors?.length ? body.errors.join(' ') : body.message;
        throw new Error(detail || 'Validation failed');
      }

      if (start.status !== 202) {
        throw new Error('Unexpected response when starting recommendation job');
      }

      const startBody = start.data as CosellingWithProfileJobStartResponse;
      if (!startBody.success || !startBody.jobId) {
        throw new Error(startBody.message || 'Failed to start recommendation job');
      }

      const jobId = startBody.jobId;
      const pollUrl = `${baseUrl}/jobs/${encodeURIComponent(jobId)}`;
      const deadline = Date.now() + WITH_PROFILE_MAX_WAIT_MS;

      while (Date.now() < deadline) {
        const poll = await axios.get<CosellingWithProfileJobPollBody>(pollUrl, {
          headers: { Authorization: `Bearer ${token}` },
          validateStatus: (status) => status === 200 || status === 404,
        });

        if (poll.status === 404) {
          const msg =
            typeof poll.data === 'object' &&
            poll.data &&
            'message' in poll.data &&
            typeof (poll.data as { message?: string }).message === 'string'
              ? (poll.data as { message: string }).message
              : 'Job not found';
          throw new Error(msg);
        }

        const body = poll.data;

        if (!body.success) {
          throw new Error(
            'message' in body ? body.message : 'Failed to poll job status',
          );
        }

        if (body.status === 'completed') {
          return {
            success: true,
            message: body.message,
            data: body.data,
            metadata: body.metadata,
          };
        }

        if (body.status === 'failed') {
          throw new Error(
            body.error?.message ||
              'Failed to generate co-selling recommendations with profile',
          );
        }

        await sleep(WITH_PROFILE_POLL_INTERVAL_MS);
      }

      throw new Error(
        'Co-selling recommendations are still processing. Please refresh the page in a few minutes.',
      );
    } catch (error) {
      console.error(
        'Error generating co-selling recommendations with profile:',
        error,
      );
      throw error;
    }
  }

  /**
   * Sort key: higher = stronger intent (Priority/High first, then Engage/Medium, etc.).
   */
  static intentStrengthSortRank(intentStrength: string): number {
    switch (cosellingIntentStrengthTier(intentStrength)) {
      case 'high':
        return 3;
      case 'medium':
        return 2;
      case 'low':
        return 1;
      default:
        return 0;
    }
  }

  /**
   * Chakra Badge `colorScheme` for intent strength (legacy + new labels).
   * Green = Priority, Yellow = Engage, Purple = Explore.
   */
  static intentStrengthBadgeColorScheme(
    intentStrength: string,
  ): 'green' | 'yellow' | 'purple' | 'gray' {
    switch (cosellingIntentStrengthTier(intentStrength)) {
      case 'high':
        return 'green';
      case 'medium':
        return 'yellow';
      case 'low':
        return 'purple';
      default:
        return 'gray';
    }
  }

  /** Always show Priority / Engage / Explore in the UI regardless of API wording. */
  static intentStrengthDisplayLabel(intentStrength: string): string {
    return cosellingIntentStrengthDisplayLabel(intentStrength);
  }

  /**
   * Chakra color token for intent tier (matches badge scheme).
   */
  static getPriorityColor(intentStrength: string): string {
    switch (cosellingIntentStrengthTier(intentStrength)) {
      case 'high':
        return 'green.500';
      case 'medium':
        return 'yellow.500';
      case 'low':
        return 'purple.500';
      default:
        return 'gray.500';
    }
  }

  /**
   * Get value color for pipeline score
   */
  static getValueColor(pipelineScore: number): string {
    if (pipelineScore >= 80) return 'green.600';
    if (pipelineScore >= 60) return 'green.500';
    if (pipelineScore >= 40) return 'yellow.500';
    return 'red.500';
  }

  /**
   * Format priority text for display
   */
  static formatPriority(
    confidencePercent: CosellingRecommendation['confidencePercent'],
  ): string {
    return confidencePercent.toString();
  }

  /**
   * Format value text for display
   */
  static formatValue(pipelineScore: number): string {
    return `${pipelineScore}%`;
  }
}

export const cosellingService = CosellingService;
